/**
 * Integrytas Group - Unified Notification Engine (UNE)
 * Page: UNE.PREFERENCES
 *
 * The user preference matrix: per event category, choose In-App Only / Email
 * Only / Both / None — scoped to the active workspace. Channels an administrator
 * marked Mandatory are locked on (the conflicting options are disabled).
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { useActiveWorkspace } from '../../lib/use_workspace';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import type { AuthSession } from '../../types/auth';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';

type Pref = 'INAPP_ONLY' | 'EMAIL_ONLY' | 'BOTH' | 'NONE';
const PREFS: { value: Pref; label: string; inapp: boolean; email: boolean }[] = [
  { value: 'INAPP_ONLY', label: 'In-App Only', inapp: true, email: false },
  { value: 'EMAIL_ONLY', label: 'Email Only', inapp: false, email: true },
  { value: 'BOTH', label: 'Both', inapp: true, email: true },
  { value: 'NONE', label: 'None', inapp: false, email: false },
];
interface Category { une_event_category_id: number; internal_code: string; name: string }
interface Mandatory { une_event_category_id: number; channel: 'INAPP' | 'EMAIL' }

const PREF_GRID_COLS: GridColumnDef[] = [
  { key: 'category',    header: 'Event Category', defaultWidth: 260 },
  { key: 'INAPP_ONLY',  header: 'In-App Only',    defaultWidth: 120 },
  { key: 'EMAIL_ONLY',  header: 'Email Only',     defaultWidth: 120 },
  { key: 'BOTH',        header: 'Both',           defaultWidth: 120 },
  { key: 'NONE',        header: 'None',           defaultWidth: 120 },
  { key: 'locked',      header: 'Locked',         defaultWidth: 180 },
];

export interface UnePreferenceCenterPageProps { session: AuthSession }

/* rbac-affordance-ok: self-service. Every write here is the signed-in
   user's OWN une_user_preference row (user_id = session.effective_user.user_id),
   not page-scoped data, so csm_role_page_access.can_change is the wrong gate —
   applying it would stop a user setting their own notification preferences. */
export function UnePreferenceCenterPage({ session }: UnePreferenceCenterPageProps) {
  return (
    <PersonalizationProvider pageCode="UNE.PREFERENCES" session={session}>
      <UnePreferenceCenterPageInner session={session} />
    </PersonalizationProvider>
  );
}

function UnePreferenceCenterPageInner({ session }: UnePreferenceCenterPageProps) {
  const { t } = useTranslation();
  const userId = session.effective_user.user_id;
  const { workspaces, activeCode, activeId, setActive } = useActiveWorkspace();
  const [categories, setCategories] = useState<Category[]>([]);
  const [mandatory, setMandatory] = useState<Mandatory[]>([]);
  const [prefs, setPrefs] = useState<Record<number, Pref>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(c => c.name.toLowerCase().includes(q) || c.internal_code.toLowerCase().includes(q));
  }, [categories, filter]);

  // §17.B13: expand/contract control. A preference row is a per-category choice matrix
  // with no obvious child collection loaded here, so the control renders inert.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [/* TODO: wire relations */];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const [c, m, p] = await Promise.all([
        apiDb.from('une_event_category').select('une_event_category_id, internal_code, name').eq('is_active', 'Y').order('sort_order'),
        apiDb.from('une_mandatory_rule').select('une_event_category_id, channel, une_workspace_id').eq('is_active', 'Y'),
        apiDb.from('une_user_preference').select('une_event_category_id, channel_pref, une_workspace_id').eq('user_id', userId),
      ]);
      if (c.error) throw c.error;
      setCategories((c.data ?? []) as Category[]);
      setMandatory(((m.data ?? []) as (Mandatory & { une_workspace_id: number | null })[]).filter(r => r.une_workspace_id == null || r.une_workspace_id === activeId));
      const pm: Record<number, Pref> = {};
      ((p.data ?? []) as { une_event_category_id: number; channel_pref: Pref; une_workspace_id: number | null }[])
        .filter(r => r.une_workspace_id === activeId).forEach(r => { pm[r.une_event_category_id] = r.channel_pref; });
      setPrefs(pm);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }, [userId, activeId]);
  useEffect(() => { if (activeId != null) void reload(); }, [activeId, reload]);

  const forced = (catId: number) => ({
    inapp: mandatory.some(m => m.une_event_category_id === catId && m.channel === 'INAPP'),
    email: mandatory.some(m => m.une_event_category_id === catId && m.channel === 'EMAIL'),
  });
  const allowed = (catId: number, opt: typeof PREFS[number]) => {
    const f = forced(catId);
    if (f.inapp && !opt.inapp) return false;
    if (f.email && !opt.email) return false;
    return true;
  };

  const setPref = async (catId: number, value: Pref) => {
    if (activeId == null) return;
    setPrefs(p => ({ ...p, [catId]: value }));
    setNotice(null); setError(null);
    try {
      // §2: the upsert keyed on the (user, category, workspace) unique — not the PK —
      // so resolve the existing row's surrogate PK first, then route ADD vs CHANGE.
      const { data: existing, error: eFind } = await apiDb.from('une_user_preference')
        .select('une_user_preference_id')
        .eq('user_id', userId)
        .eq('une_event_category_id', catId)
        .eq('une_workspace_id', activeId)
        .maybeSingle();
      if (eFind) throw eFind;
      const existingId = (existing as { une_user_preference_id: number } | null)?.une_user_preference_id ?? null;
      const { error: e } = existingId != null
        ? await routedUpdate(session, 'une_user_preference', 'une_user_preference_id', existingId, { channel_pref: value })
        : await routedInsert(session, 'une_user_preference', {
            user_id: userId, une_workspace_id: activeId, une_event_category_id: catId, channel_pref: value,
          });
      if (e) throw e;
      setNotice(t('une_prefs.saved', 'Saved.'));
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('une_prefs.title', 'Notification Preferences')}</span>
        <div className="w-48">
          <FilteredModalSingleSelect
            label={t('une_prefs.workspace', 'Workspace')}
            options={workspaces.map(w => ({ internal_code: w.internal_code, display_text: w.name }))}
            selectedCode={activeCode || null}
            onChange={(code) => setActive(code ?? activeCode)}
            allowClear={false}
          />
        </div>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('une_prefs.filter_categories', 'Filter categories…')}
          className="h-7 w-56 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        {busy && <span className="text-csm-xs text-csm-watermark">{t('une_prefs.working', 'Working…')}</span>}
        {notice && <span className="text-csm-xs text-primary">{notice}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('une_prefs.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        {busy && categories.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={PREF_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-3 py-2 text-left" title={t('une_prefs.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className={'border-b border-csm-border px-3 py-2 ' + (col.key === 'category' || col.key === 'locked' ? 'text-left' : 'text-center')}
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('une_prefs.col.' + col.key, col.header)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(cat => {
                  const f = forced(cat.une_event_category_id);
                  const current = prefs[cat.une_event_category_id] ?? 'BOTH';
                  return (
                    <Fragment key={cat.une_event_category_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton open={detail.isOpen(cat.une_event_category_id)} hasChildren={hasRelated(cat.une_event_category_id)} onToggle={() => detail.toggle(cat.une_event_category_id)} expandLabel={t('une_prefs.related.expand', 'Show related records')} collapseLabel={t('une_prefs.related.collapse', 'Hide related')} tooltip={t('une_prefs.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => {
                        const opt = PREFS.find(p => p.value === col.key);
                        return (
                          <td key={col.key}
                              className={'border-b border-csm-border-soft px-3 py-2 ' + (col.key === 'category' ? 'text-csm-sm' : col.key === 'locked' ? '' : 'text-center')}
                              style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                            {col.key === 'category' && cat.name}
                            {opt && (() => {
                              const ok = allowed(cat.une_event_category_id, opt);
                              return (
                                <input type="radio" name={`pref_${cat.une_event_category_id}`} checked={current === opt.value} disabled={!ok}
                                       onChange={() => void setPref(cat.une_event_category_id, opt.value)} className="h-4 w-4 accent-primary disabled:opacity-30" />
                              );
                            })()}
                            {col.key === 'locked' && (f.inapp || f.email) && (
                              <span className="rounded bg-csm-danger-soft px-1.5 py-0.5 text-csm-2xs text-csm-danger" title={t('une_prefs.locked_by_admin', 'Locked by administrator')}>
                                {t('une_prefs.mandatory', 'Mandatory:')} {[f.inapp && t('une_prefs.channel_inapp', 'In-App'), f.email && t('une_prefs.channel_email', 'Email')].filter(Boolean).join(' + ')}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {detail.isOpen(cat.une_event_category_id) && (
                      <tr><td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(cat.une_event_category_id)} session={session} />
                      </td></tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 1} className="px-3 py-3">
                    <EmptyState variant={filter.trim() ? 'no-match' : 'no-data'} title={t('une_prefs.empty', 'No event categories.')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
        <p className="mt-3 text-csm-2xs text-csm-watermark">{t('une_prefs.footer_before', 'Preferences apply to the')} <strong>{workspaces.find(w => w.internal_code === activeCode)?.name ?? '—'}</strong> {t('une_prefs.footer_after', 'workspace. Switch workspace above to set different preferences. In-App covers both real-time toasts and your inbox.')}</p>
      </div>
    </div>
  );
}
